import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';

const BEDROOM_VALUES: number[] = [0, 1, 2, 3, 4]; // 0=Studio, 4=4+

function labelFor(value: number) {
  if (value === 0) return 'Studio';
  if (value === 4) return '4+';
  return String(value);
}

// Shared hook for bedroom filter logic - exported for reuse
export function useBedroomFilter() {
  const analytics = useAnalytics();
  const bedrooms = useStore(s => s.listingsFilters.bedrooms);
  const toggleBedroomRaw = useStore(s => s.listingsFilters.toggleBedroom);

  const toggleBedroom = (value: number) => {
    analytics.track('listings_filter_beds', { bed: value });
    toggleBedroomRaw(value);
  };

  const formatBedroomsDisplay = () => {
    if (bedrooms.length === 0) {
      return undefined; // Show placeholder
    }

    const sorted = [...bedrooms].sort((a, b) => a - b);

    // Single selection
    if (sorted.length === 1) {
      const v = sorted[0];
      if (v === 0) return 'Studio';
      if (v === 1) return '1 bed';
      if (v === 4) return '4+ beds';
      return `${v} beds`;
    }

    // Helpers for formatting labels
    const formatEndpoint = (v: number | undefined) => {
      if (v === undefined) return '';
      if (v === 0) return 'Studio';
      if (v === 4) return '4+';
      return String(v);
    };

    const isContinuous = sorted.every((v, idx) => idx === 0 || v - (sorted[idx - 1] ?? 0) === 1);

    // Continuous range → endpoints only
    if (isContinuous) {
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const suffix = last === 1 ? 'bed' : 'beds';
      return `${formatEndpoint(first)}-${formatEndpoint(last)} ${suffix}`;
    }

    // Non-continuous → comma-separated list, always plural suffix
    const labels = sorted.map(v => (v === 0 ? 'Studio' : v === 4 ? '4+' : String(v)));
    return `${labels.join(', ')} beds`;
  };

  return {
    bedrooms,
    toggleBedroom,
    formatBedroomsDisplay,
    bedroomValues: BEDROOM_VALUES,
    labelFor,
  };
}
